import React, { useState } from "react";
import PropTypes from "prop-types";
import { Autocomplete, TextField } from "@mui/material";
import { ChevronDown, ChevronUp } from "lucide-react";
import { UIFieldShell, useUIField } from "../form";
import { clx } from "../../../utils/Helpers";
import "./UISelect.scss";

const hasValue = (value) =>
  Array.isArray(value) ? value.length > 0 : Boolean(value);

const defaultGetOptionLabel = (option) => {
  if (!option) return "";
  if (typeof option === "string") return option;
  return option.name || option.label || "";
};

const UISelect = ({
  label,
  optionalLabel,
  required,
  helperText,
  error,
  id,
  name,
  placeholder,
  options,
  value,
  multiple,
  onChange,
  onBlur,
  getOptionLabel,
  isOptionEqualToValue,
  textFieldProps,
  popupIcon,
  disabled = false,
  className,
  componentsProps,
  sx,
  open,
  onOpen,
  onClose,
  size,
  ...props
}) => {
  const [isOpenInternal, setIsOpenInternal] = useState(false);
  const fieldId = id || name;
  const field = useUIField({
    name,
    value,
    error,
    helperText,
  });

  const resolvedValue = multiple ? field.value || [] : field.value || null;
  const isControlledOpen = typeof open === "boolean";
  const isOpen = isControlledOpen ? open : isOpenInternal;

  const handleOpen = (event) => {
    if (!isControlledOpen) {
      setIsOpenInternal(true);
    }
    onOpen?.(event);
  };

  const handleClose = (event, reason) => {
    if (!isControlledOpen) {
      setIsOpenInternal(false);
    }
    onClose?.(event, reason);
  };

  const autocompleteSx = [
    ...(Array.isArray(sx) ? sx : sx != null ? [sx] : []),
    ...(disabled
      ? [
          {
            pointerEvents: "none",
            cursor: "default",
            opacity: 1,
          },
        ]
      : []),
  ].filter(Boolean);

  return (
    <UIFieldShell
      id={fieldId}
      label={label}
      optionalLabel={optionalLabel}
      required={required}
      helperText={field.helperText}
      error={field.showError}
    >
      <Autocomplete
        id={fieldId}
        disabled={disabled}
        multiple={multiple}
        options={options}
        value={resolvedValue}
        open={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        popupIcon={
          popupIcon || (
            isOpen ? (
              <ChevronUp
                size={18}
                color="var(--color-foreground-subtle)"
                aria-hidden="true"
              />
            ) : (
              <ChevronDown
                size={18}
                color="var(--color-foreground-subtle)"
                aria-hidden="true"
              />
            )
          )
        }
        className={clx(
          "ui-select",
          "ui-input",
          hasValue(resolvedValue) && "ui-input--filled",
          isOpen && "ui-select--open",
          size === "large" && "ui-input--large",
          className,
        )}
        sx={autocompleteSx.length ? autocompleteSx : undefined}
        onChange={(_event, nextValue) => {
          // When consumers pass `onChange`, they typically write primitives via
          // `setFieldValue`; skip `field.setValue` so we don't briefly persist the
          // full option object on the named Formik path (breaks Yup / displays).
          if (onChange) {
            onChange(nextValue);
          } else {
            field.setValue(nextValue);
          }
        }}
        onBlur={(event) => {
          field.setTouched(true);
          onBlur?.(event);
        }}
        componentsProps={{
          ...componentsProps,
          paper: {
            ...componentsProps?.paper,
            className: clx(
              "ui-select__paper",
              componentsProps?.paper?.className,
            ),
          },
          popper: {
            ...componentsProps?.popper,
            className: clx(
              "ui-select__popper",
              componentsProps?.popper?.className,
            ),
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            {...textFieldProps}
            name={name}
            placeholder={placeholder}
            required={required}
            error={field.showError}
            helperText={null}
            size={size}
            inputProps={{
              ...params.inputProps,
              ...textFieldProps?.inputProps,
            }}
            InputProps={{
              ...params.InputProps,
              ...textFieldProps?.InputProps,
            }}
          />
        )}
        {...props}
      />
    </UIFieldShell>
  );
};

UISelect.propTypes = {
  label: PropTypes.node,
  optionalLabel: PropTypes.node,
  required: PropTypes.bool,
  helperText: PropTypes.node,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.array,
  value: PropTypes.any,
  multiple: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  getOptionLabel: PropTypes.func,
  isOptionEqualToValue: PropTypes.func,
  textFieldProps: PropTypes.object,
  popupIcon: PropTypes.node,
  className: PropTypes.string,
  componentsProps: PropTypes.object,
  open: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  size: PropTypes.oneOf(["medium", "large"]),
  sx: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.object, PropTypes.bool, PropTypes.func]),
    ),
  ]),
};

UISelect.defaultProps = {
  label: null,
  optionalLabel: null,
  required: false,
  helperText: null,
  error: undefined,
  disabled: false,
  id: undefined,
  name: undefined,
  placeholder: undefined,
  options: [],
  value: null,
  multiple: false,
  onChange: undefined,
  onBlur: undefined,
  getOptionLabel: defaultGetOptionLabel,
  isOptionEqualToValue: (option, selected) => option?.id === selected?.id,
  textFieldProps: {},
  popupIcon: null,
  className: undefined,
  componentsProps: undefined,
  open: undefined,
  onOpen: undefined,
  onClose: undefined,
  size: "medium",
  sx: undefined,
};

export default UISelect;
